import type { Middleware, MiddlewareAPI } from "@reduxjs/toolkit";
import { isRejectedWithValue } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import i18n from "i18next";

import { showErrorNotification } from "../../../components/NotificationAlerts";
import { formatValidationErrors, isLegacyValidationErrorFormat, isNewValidationErrorFormat } from "../../errorHandling";

const rtkQueryErrorLogger: Middleware = (_api: MiddlewareAPI) => (next) => (action) => {
    if (isRejectedWithValue(action)) {
        const payload = action.payload as FetchBaseQueryError | undefined;
        const errorData = payload?.data as { message?: string; error?: string } | undefined;

        const isParsingError = payload?.status === "PARSING_ERROR";
        const isGatewayTimeout = payload?.status === "PARSING_ERROR" && [502, 503, 504].includes(payload.originalStatus);

        // Check if this is a validation error (new or old format)
        let errorText: string;
        if (isNewValidationErrorFormat(errorData) || isLegacyValidationErrorFormat(errorData)) {
            errorText = formatValidationErrors(errorData, (key: string) => i18n.t(key));
        } else if (isGatewayTimeout) {
            // nginx (or another reverse proxy) gave up waiting on the backend and returned its own
            // HTML error page instead of JSON. Tell the user the request timed out, not that JSON failed to parse.
            errorText = i18n.t("common.requestTimeout");
        } else if (isParsingError) {
            // A non-JSON response body (e.g. an HTML error/login page from an expired session or a backend
            // 500) makes fetchBaseQuery's own error.error a raw JS SyntaxError message - never show that
            // verbatim to the user.
            errorText = i18n.t("common.error");
        } else {
            const payloadError = payload && "error" in payload ? payload.error : undefined;
            errorText = payloadError || errorData?.message || errorData?.error || i18n.t("common.error");
        }

        const notificationPayload = {
            text: errorText,
            data: errorData,
            autoCloseDisabled: true,
        };

        _api.dispatch(showErrorNotification(notificationPayload));
    }

    return next(action);
};

export default rtkQueryErrorLogger;
